import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  Calendar, Users, Store, Clock, CheckSquare, DollarSign, Plus, Edit2, Trash2,
  ChevronRight, CheckCircle, AlertCircle, MapPin, Phone, Mail, X, Save,
  PartyPopper, Building2, Cake, User, FileText
} from 'lucide-react';
import { apiErrorMessage } from './utils/apiError';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Event Types with icons
const EVENT_TYPES = {
  wedding: { label: 'Wedding', icon: '💒', color: 'pink' },
  corporate: { label: 'Corporate', icon: '🏢', color: 'blue' },
  birthday: { label: 'Birthday', icon: '🎂', color: 'purple' },
  conference: { label: 'Conference', icon: '📊', color: 'green' },
  party: { label: 'Party', icon: '🎉', color: 'orange' },
  festival: { label: 'Festival', icon: '🎪', color: 'red' },
  catering_only: { label: 'Catering Only', icon: '🍽️', color: 'teal' },
  other: { label: 'Other', icon: '📅', color: 'gray' }
};

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-600',
  planning: 'bg-blue-100 text-blue-600',
  confirmed: 'bg-green-100 text-green-600',
  in_progress: 'bg-yellow-100 text-yellow-600',
  completed: 'bg-emerald-100 text-emerald-600',
  cancelled: 'bg-red-100 text-red-600'
};

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-orange-100 text-orange-600',
  urgent: 'bg-red-100 text-red-600'
};

// Spinner Component
const Spinner = () => (
  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
);

// Modal Component
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

// Events List Page
export const EventsListPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [platformHeaders, setPlatformHeaders] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setEvents([]);
        return;
      }

      const ctxRes = await fetch(`${API_URL}/api/platform/context`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const ctxJson = await ctxRes.json();
      const orgId = ctxJson?.organizations?.[0]?.id;
      const locId = ctxJson?.organizations?.[0]?.locations?.[0]?.id;

      const headers = {
        Authorization: `Bearer ${token}`,
        ...(orgId ? { 'X-Organization-Id': orgId } : {}),
        ...(locId ? { 'X-Location-Id': locId } : {})
      };
      setPlatformHeaders(headers);

      if (orgId) {
        await fetchEvents(headers);
      } else {
        console.warn("No organization context found, skipping event fetch");
        setEvents([]);
      }
    } catch (err) {
      console.error("Init error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async (headers) => {
    try {
      const res = await fetch(`${API_URL}/api/events`, { headers });
      if (!res.ok) {
          console.error("Failed to fetch events:", res.status);
          return;
      }
      const data = await res.json();
      if (Array.isArray(data)) {
        setEvents(data);
      } else {
        setEvents([]);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const days = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'Past';
    if (days === 0) return 'Today!';
    if (days === 1) return 'Tomorrow';
    return `${days} days`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Event Planning</h1>
            <p className="text-gray-600">Manage your events, guests, and vendors</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="bg-primary-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-600 transition"
            data-testid="create-event-btn"
          >
            <Plus className="w-5 h-5" /> New Event
          </button>
        </div>

        {events.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <PartyPopper className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No events yet</h2>
            <p className="text-gray-600 mb-6">Create your first event to get started with planning</p>
            <button
              onClick={() => setShowCreate(true)}
              className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition"
            >
              Create Your First Event
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map(event => (
              <div
                key={event.id}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition cursor-pointer overflow-hidden"
                onClick={() => navigate(`/events/${event.id}`)}
                data-testid={`event-card-${event.id}`}
              >
                <div className={`h-2 ${event.event_type === 'wedding' ? 'bg-pink-500' : event.event_type === 'corporate' ? 'bg-blue-500' : event.event_type === 'birthday' ? 'bg-purple-500' : 'bg-orange-500'}`}></div>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <span className="text-2xl mr-2">{EVENT_TYPES[event.event_type]?.icon || '📅'}</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLORS[event.status]}`}>
                        {event.status?.replace('_', ' ')}
                      </span>
                    </div>
                    {getDaysUntil(event.date) && (
                      <span className={`text-sm font-medium ${getDaysUntil(event.date) === 'Past' ? 'text-gray-400' : 'text-primary-500'}`}>
                        {getDaysUntil(event.date)}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{event.name}</h3>
                  <div className="flex items-center text-gray-500 text-sm mb-3">
                    <Calendar className="w-4 h-4 mr-1" />
                    {formatDate(event.date)}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center text-gray-500">
                      <Users className="w-4 h-4 mr-1" />
                      {event.expected_guests || 0} guests
                    </span>
                    {event.budget_total > 0 && (
                      <span className="flex items-center text-gray-500">
                        <DollarSign className="w-4 h-4 mr-1" />
                        £{event.budget_total.toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Event Modal */}
        <CreateEventModal
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
          platformHeaders={platformHeaders}
          onCreated={(event) => {
            setEvents([event, ...events]);
            setShowCreate(false);
            navigate(`/events/${event.id}`);
          }}
        />
      </div>
    </div>
  );
};

// Create Event Modal
const CreateEventModal = ({ isOpen, onClose, onCreated, platformHeaders }) => {
  const [form, setForm] = useState({
    name: '',
    event_type: 'party',
    date: '',
    expected_guests: 50,
    budget_total: 0,
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(platformHeaders || {})
        },
        body: JSON.stringify({
          ...form,
          date: new Date(form.date).toISOString()
        })
      });
      if (res.ok) {
        const data = await res.json();
        onCreated(data);
      } else {
        const err = await res.json().catch(() => ({}));
        setError(apiErrorMessage(err.detail, `Could not create event (${res.status})`));
      }
    } catch (err) {
      console.error('Error creating event:', err);
      setError('Network error — could not reach the server.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create New Event">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2" role="alert">
            {error}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Event Name *</label>
          <input aria-label="Event Name"
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="e.g., Sarah's Wedding"
            data-testid="event-name-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
          <select aria-label="Event Type"
            value={form.event_type}
            onChange={(e) => setForm({ ...form, event_type: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            {Object.entries(EVENT_TYPES).map(([key, { label, icon }]) => (
              <option key={key} value={key}>{icon} {label}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
            <input aria-label="Date"
              type="datetime-local"
              required
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              data-testid="event-date-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expected Guests</label>
            <input aria-label="Expected Guests"
              type="number"
              value={form.expected_guests}
              onChange={(e) => setForm({ ...form, expected_guests: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Budget (£)</label>
          <input aria-label="Budget (£)"
            type="number"
            value={form.budget_total}
            onChange={(e) => setForm({ ...form, budget_total: parseFloat(e.target.value) || 0 })}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="e.g., 5000"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea aria-label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            rows={3}
            placeholder="Add any notes about the event..."
          />
        </div>
        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-primary-500 text-white py-2 rounded-lg hover:bg-primary-600 transition flex items-center justify-center"
            data-testid="create-event-submit"
          >
            {loading ? <Spinner /> : 'Create Event'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Event Detail Page
export const EventDetailPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [showAddChecklist, setShowAddChecklist] = useState(false);
  const [actionError, setActionError] = useState(null);

  useEffect(() => {
    fetchEventData();
  }, [eventId]);

  const fetchEventData = async () => {
    try {
      const [eventRes, dashboardRes] = await Promise.all([
        fetch(`${API_URL}/api/events/${eventId}`),
        fetch(`${API_URL}/api/events/${eventId}/dashboard`)
      ]);
      setEvent(await eventRes.json());
      setDashboard(await dashboardRes.json());
    } catch (err) {
      console.error('Error fetching event:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateChecklist = async () => {
    setActionError(null);
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/checklist/generate`, { method: 'POST' });
      if (res.ok) {
        fetchEventData();
      } else {
        const err = await res.json().catch(() => ({}));
        setActionError(apiErrorMessage(err.detail, `Could not generate checklist (${res.status})`));
      }
    } catch (err) {
      console.error('Error generating checklist:', err);
      setActionError('Network error — could not reach the server.');
    }
  };

  const updateChecklistItem = async (itemId, completed) => {
    setActionError(null);
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/checklist/${itemId}?completed=${completed}`, {
        method: 'PUT'
      });
      if (res.ok) {
        fetchEventData();
      } else {
        const err = await res.json().catch(() => ({}));
        setActionError(apiErrorMessage(err.detail, `Could not update checklist item (${res.status})`));
      }
    } catch (err) {
      console.error('Error updating checklist:', err);
      setActionError('Network error — could not reach the server.');
    }
  };

  const updateGuestRSVP = async (guestId, status) => {
    setActionError(null);
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/guests/${guestId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rsvp_status: status })
      });
      if (res.ok) {
        fetchEventData();
      } else {
        const err = await res.json().catch(() => ({}));
        setActionError(apiErrorMessage(err.detail, `Could not update guest RSVP (${res.status})`));
      }
    } catch (err) {
      console.error('Error updating guest:', err);
      setActionError('Network error — could not reach the server.');
    }
  };

  const deleteEvent = async () => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    setActionError(null);
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}`, { method: 'DELETE' });
      if (res.ok) {
        navigate('/events');
      } else {
        const err = await res.json().catch(() => ({}));
        setActionError(apiErrorMessage(err.detail, `Could not delete event (${res.status})`));
      }
    } catch (err) {
      console.error('Error deleting event:', err);
      setActionError('Network error — could not reach the server.');
    }
  };

  if (loading || !event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FileText },
    { id: 'guests', label: 'Guests', icon: Users, count: dashboard?.guests?.total },
    { id: 'vendors', label: 'Vendors', icon: Store, count: dashboard?.vendors?.total },
    { id: 'checklist', label: 'Checklist', icon: CheckSquare, count: dashboard?.checklist?.total },
    { id: 'timeline', label: 'Timeline', icon: Clock },
    { id: 'budget', label: 'Budget', icon: DollarSign }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Link to="/events" className="hover:text-primary-500">Events</Link>
            <ChevronRight className="w-4 h-4" />
            <span>{event.name}</span>
          </div>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">{EVENT_TYPES[event.event_type]?.icon}</span>
                <h1 className="text-2xl font-bold text-gray-900">{event.name}</h1>
                <span className={`text-sm px-3 py-1 rounded-full ${STATUS_COLORS[event.status]}`}>
                  {event.status?.replace('_', ' ')}
                </span>
              </div>
              <div className="flex items-center gap-6 text-gray-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(event.date).toLocaleDateString('en-GB', {
                    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                  })}
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {event.expected_guests} expected
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={deleteEvent}
                className="border border-red-300 text-red-600 px-4 py-2 rounded-lg hover:bg-red-50 transition"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.count > 0 && (
                  <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {actionError && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 flex items-center justify-between" role="alert">
            <span>{actionError}</span>
            <button onClick={() => setActionError(null)} className="text-red-400 hover:text-red-600 ml-4">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {/* Overview Tab */}
        {activeTab === 'overview' && dashboard && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500">Confirmed Guests</span>
                <Users className="w-5 h-5 text-green-500" />
              </div>
              <div className="text-3xl font-bold text-gray-900">
                {dashboard.guests.confirmed}/{dashboard.guests.total}
              </div>
              <div className="text-sm text-gray-500">
                {dashboard.guests.total_with_plus_ones} total with +1s
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500">Vendors</span>
                <Store className="w-5 h-5 text-blue-500" />
              </div>
              <div className="text-3xl font-bold text-gray-900">
                {dashboard.vendors.confirmed}/{dashboard.vendors.total}
              </div>
              <div className="text-sm text-gray-500">
                £{dashboard.vendors.confirmed_total.toLocaleString()} confirmed
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500">Checklist</span>
                <CheckSquare className="w-5 h-5 text-purple-500" />
              </div>
              <div className="text-3xl font-bold text-gray-900">
                {dashboard.checklist.completed}/{dashboard.checklist.total}
              </div>
              <div className="text-sm text-red-500">
                {dashboard.checklist.overdue > 0 && `${dashboard.checklist.overdue} overdue`}
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500">Budget</span>
                <DollarSign className="w-5 h-5 text-orange-500" />
              </div>
              <div className="text-3xl font-bold text-gray-900">
                £{dashboard.budget.actual_total.toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">
                of £{dashboard.budget.total_budget.toLocaleString()} budget
              </div>
            </div>
          </div>
        )}

        {/* Guests Tab */}
        {activeTab === 'guests' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Guest List</h2>
              <button
                onClick={() => setShowAddGuest(true)}
                className="bg-primary-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-600"
                data-testid="add-guest-btn"
              >
                <Plus className="w-4 h-4" /> Add Guest
              </button>
            </div>
            {event.guests?.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No guests added yet</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Name</th>
                      <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Contact</th>
                      <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">RSVP</th>
                      <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Dietary</th>
                      <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {event.guests?.map(guest => (
                      <tr key={guest.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{guest.name}</div>
                          {guest.plus_ones > 0 && (
                            <div className="text-sm text-gray-500">+{guest.plus_ones} guests</div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {guest.email && <div>{guest.email}</div>}
                          {guest.phone && <div>{guest.phone}</div>}
                        </td>
                        <td className="px-6 py-4">
                          <select
                            value={guest.rsvp_status}
                            onChange={(e) => updateGuestRSVP(guest.id, e.target.value)}
                            className={`text-sm px-2 py-1 rounded-full border-0 ${
                              guest.rsvp_status === 'confirmed' ? 'bg-green-100 text-green-600' :
                              guest.rsvp_status === 'declined' ? 'bg-red-100 text-red-600' :
                              'bg-gray-100 text-gray-600'
                            }`}
                          >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="declined">Declined</option>
                            <option value="maybe">Maybe</option>
                          </select>
                        </td>
                        <td className="px-6 py-4">
                          {guest.dietary_requirements?.map((d, i) => (
                            <span key={i} className="inline-block bg-yellow-100 text-yellow-700 text-xs px-2 py-1 rounded mr-1">
                              {d}
                            </span>
                          ))}
                        </td>
                        <td className="px-6 py-4">
                          <button className="text-gray-400 hover:text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Vendors Tab */}
        {activeTab === 'vendors' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Vendors</h2>
              <button
                onClick={() => setShowAddVendor(true)}
                className="bg-primary-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-600"
                data-testid="add-vendor-btn"
              >
                <Plus className="w-4 h-4" /> Add Vendor
              </button>
            </div>
            {event.vendors?.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center">
                <Store className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No vendors added yet</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {event.vendors?.map(vendor => (
                  <div key={vendor.id} className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">{vendor.vendor_name}</h3>
                        <span className="text-sm text-gray-500 capitalize">{vendor.vendor_type}</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        vendor.status === 'confirmed' ? 'bg-green-100 text-green-600' :
                        vendor.status === 'quoted' ? 'bg-blue-100 text-blue-600' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {vendor.status}
                      </span>
                    </div>
                    {vendor.contact_email && (
                      <div className="flex items-center text-sm text-gray-500 mb-1">
                        <Mail className="w-4 h-4 mr-2" /> {vendor.contact_email}
                      </div>
                    )}
                    {vendor.contact_phone && (
                      <div className="flex items-center text-sm text-gray-500 mb-3">
                        <Phone className="w-4 h-4 mr-2" /> {vendor.contact_phone}
                      </div>
                    )}
                    {vendor.quoted_amount > 0 && (
                      <div className="text-lg font-semibold text-gray-900">
                        £{vendor.quoted_amount.toLocaleString()}
                        {vendor.confirmed_amount && (
                          <span className="text-green-600 ml-2">(£{vendor.confirmed_amount.toLocaleString()} confirmed)</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Checklist Tab */}
        {activeTab === 'checklist' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Planning Checklist</h2>
              <div className="flex gap-2">
                {event.checklist?.length === 0 && (
                  <button
                    onClick={generateChecklist}
                    className="border border-primary-500 text-primary-500 px-4 py-2 rounded-lg hover:bg-primary-50"
                    data-testid="generate-checklist-btn"
                  >
                    Generate Template
                  </button>
                )}
                <button
                  onClick={() => setShowAddChecklist(true)}
                  className="bg-primary-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-600"
                >
                  <Plus className="w-4 h-4" /> Add Task
                </button>
              </div>
            </div>
            {event.checklist?.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center">
                <CheckSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-4">No checklist items yet</p>
                <button
                  onClick={generateChecklist}
                  className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600"
                >
                  Generate Checklist from Template
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {event.checklist?.map(item => (
                  <div
                    key={item.id}
                    className={`bg-white rounded-lg p-4 shadow-sm flex items-center gap-4 ${item.completed ? 'opacity-60' : ''}`}
                  >
                    <button
                      onClick={() => updateChecklistItem(item.id, !item.completed)}
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        item.completed ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300'
                      }`}
                    >
                      {item.completed && <CheckCircle className="w-4 h-4" />}
                    </button>
                    <div className="flex-1">
                      <div className={`font-medium ${item.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                        {item.task}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span className="capitalize">{item.category}</span>
                        {item.due_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(item.due_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${PRIORITY_COLORS[item.priority]}`}>
                      {item.priority}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Timeline Tab */}
        {activeTab === 'timeline' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Event Timeline</h2>
            </div>
            {event.timeline?.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center">
                <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No timeline items yet</p>
              </div>
            ) : (
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                <div className="space-y-4">
                  {event.timeline?.sort((a, b) => a.time.localeCompare(b.time)).map(item => (
                    <div key={item.id} className="flex gap-4 items-start">
                      <div className="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center text-sm font-medium z-10">
                        {item.time.split(':')[0]}
                      </div>
                      <div className="bg-white rounded-lg p-4 shadow-sm flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-gray-900">{item.title}</span>
                          <span className="text-sm text-gray-500">{item.time}</span>
                        </div>
                        {item.description && (
                          <p className="text-gray-500 text-sm mt-1">{item.description}</p>
                        )}
                        <div className="text-xs text-gray-400 mt-2">
                          Duration: {item.duration_minutes} mins
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Budget Tab */}
        {activeTab === 'budget' && dashboard && (
          <div>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="text-gray-500 text-sm mb-1">Total Budget</div>
                <div className="text-2xl font-bold text-gray-900">£{dashboard.budget.total_budget.toLocaleString()}</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="text-gray-500 text-sm mb-1">Estimated Spend</div>
                <div className="text-2xl font-bold text-blue-600">£{dashboard.budget.estimated_total.toLocaleString()}</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="text-gray-500 text-sm mb-1">Actual Spend</div>
                <div className="text-2xl font-bold text-green-600">£{dashboard.budget.actual_total.toLocaleString()}</div>
              </div>
            </div>
            {Object.keys(dashboard.budget.by_category || {}).length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-4">Budget by Category</h3>
                <div className="space-y-3">
                  {Object.entries(dashboard.budget.by_category).map(([category, amounts]) => (
                    <div key={category} className="flex items-center justify-between">
                      <span className="capitalize text-gray-700">{category}</span>
                      <div className="text-right">
                        <span className="text-gray-900 font-medium">£{amounts.actual.toLocaleString()}</span>
                        <span className="text-gray-400 ml-2">/ £{amounts.estimated.toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Guest Modal */}
      <AddGuestModal
        isOpen={showAddGuest}
        onClose={() => setShowAddGuest(false)}
        eventId={eventId}
        onAdded={fetchEventData}
      />

      {/* Add Vendor Modal */}
      <AddVendorModal
        isOpen={showAddVendor}
        onClose={() => setShowAddVendor(false)}
        eventId={eventId}
        onAdded={fetchEventData}
      />

      {/* Add Checklist Modal */}
      <AddChecklistModal
        isOpen={showAddChecklist}
        onClose={() => setShowAddChecklist(false)}
        eventId={eventId}
        onAdded={fetchEventData}
      />
    </div>
  );
};

// Add Guest Modal
const AddGuestModal = ({ isOpen, onClose, eventId, onAdded }) => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', dietary_requirements: [], plus_ones: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/guests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        onAdded();
        onClose();
        setForm({ name: '', email: '', phone: '', dietary_requirements: [], plus_ones: 0 });
      } else {
        const err = await res.json().catch(() => ({}));
        setError(apiErrorMessage(err.detail, `Could not add guest (${res.status})`));
      }
    } catch (err) {
      console.error('Error adding guest:', err);
      setError('Network error — could not reach the server.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Guest">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2" role="alert">
            {error}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
          <input aria-label="Name"
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
            data-testid="guest-name-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input aria-label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input aria-label="Phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Plus Ones</label>
          <input aria-label="Plus Ones"
            type="number"
            min="0"
            value={form.plus_ones}
            onChange={(e) => setForm({ ...form, plus_ones: parseInt(e.target.value) || 0 })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Dietary Requirements</label>
          <div className="flex flex-wrap gap-2">
            {['vegetarian', 'vegan', 'halal', 'kosher', 'gluten_free', 'nut_allergy'].map(diet => (
              <label key={diet} className="flex items-center gap-1">
                <input aria-label="Dietary Requirements"
                  type="checkbox"
                  checked={form.dietary_requirements.includes(diet)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setForm({ ...form, dietary_requirements: [...form.dietary_requirements, diet] });
                    } else {
                      setForm({ ...form, dietary_requirements: form.dietary_requirements.filter(d => d !== diet) });
                    }
                  }}
                  className="rounded"
                />
                <span className="text-sm capitalize">{diet.replace('_', ' ')}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="flex gap-3 pt-4">
          <button type="button" onClick={onClose} className="flex-1 border py-2 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={loading} className="flex-1 bg-primary-500 text-white py-2 rounded-lg hover:bg-primary-600">
            {loading ? <Spinner /> : 'Add Guest'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Add Vendor Modal
const AddVendorModal = ({ isOpen, onClose, eventId, onAdded }) => {
  const [form, setForm] = useState({
    vendor_name: '', vendor_type: 'caterer', contact_email: '', contact_phone: '', quoted_amount: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/vendors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        onAdded();
        onClose();
        setForm({ vendor_name: '', vendor_type: 'caterer', contact_email: '', contact_phone: '', quoted_amount: 0 });
      } else {
        const err = await res.json().catch(() => ({}));
        setError(apiErrorMessage(err.detail, `Could not add vendor (${res.status})`));
      }
    } catch (err) {
      console.error('Error adding vendor:', err);
      setError('Network error — could not reach the server.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Vendor">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2" role="alert">
            {error}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name *</label>
          <input aria-label="Vendor Name"
            type="text"
            required
            value={form.vendor_name}
            onChange={(e) => setForm({ ...form, vendor_name: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
            data-testid="vendor-name-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Type</label>
          <select aria-label="Vendor Type"
            value={form.vendor_type}
            onChange={(e) => setForm({ ...form, vendor_type: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
          >
            <option value="caterer">Caterer</option>
            <option value="photographer">Photographer</option>
            <option value="videographer">Videographer</option>
            <option value="florist">Florist</option>
            <option value="dj">DJ</option>
            <option value="band">Band/Music</option>
            <option value="decorator">Decorator</option>
            <option value="venue">Venue</option>
            <option value="transport">Transport</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
            <input aria-label="Contact Email"
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
            <input aria-label="Contact Phone"
              type="tel"
              value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Quoted Amount (£)</label>
          <input aria-label="Quoted Amount (£)"
            type="number"
            value={form.quoted_amount}
            onChange={(e) => setForm({ ...form, quoted_amount: parseFloat(e.target.value) || 0 })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
        <div className="flex gap-3 pt-4">
          <button type="button" onClick={onClose} className="flex-1 border py-2 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={loading} className="flex-1 bg-primary-500 text-white py-2 rounded-lg hover:bg-primary-600">
            {loading ? <Spinner /> : 'Add Vendor'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Add Checklist Modal
const AddChecklistModal = ({ isOpen, onClose, eventId, onAdded }) => {
  const [form, setForm] = useState({
    task: '', category: 'logistics', due_date: '', priority: 'medium'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/checklist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        onAdded();
        onClose();
        setForm({ task: '', category: 'logistics', due_date: '', priority: 'medium' });
      } else {
        const err = await res.json().catch(() => ({}));
        setError(apiErrorMessage(err.detail, `Could not add checklist item (${res.status})`));
      }
    } catch (err) {
      console.error('Error adding checklist item:', err);
      setError('Network error — could not reach the server.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Checklist Item">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2" role="alert">
            {error}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Task *</label>
          <input aria-label="Task"
            type="text"
            required
            value={form.task}
            onChange={(e) => setForm({ ...form, task: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="e.g., Book venue"
            data-testid="checklist-task-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select aria-label="Category"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="venue">Venue</option>
              <option value="catering">Catering</option>
              <option value="decorations">Decorations</option>
              <option value="entertainment">Entertainment</option>
              <option value="guests">Guests</option>
              <option value="logistics">Logistics</option>
              <option value="vendors">Vendors</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select aria-label="Priority"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
          <input aria-label="Due Date"
            type="date"
            value={form.due_date}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
        <div className="flex gap-3 pt-4">
          <button type="button" onClick={onClose} className="flex-1 border py-2 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={loading} className="flex-1 bg-primary-500 text-white py-2 rounded-lg hover:bg-primary-600">
            {loading ? <Spinner /> : 'Add Task'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EventsListPage;
